import type { Router } from 'vue-router'
import { ElMessage } from 'element-plus'
import { enterBConsole, type BConsoleBindingVO } from '@/api/merchant/b-console'
import { getWorkbenchContext } from '@/api/merchant/workbench'
import { moduleIdFromConsole } from '@/views/merchant/registry/modules'
import {
  setMerchantSession,
  clearMerchantSession
} from '@/views/merchant/registry/merchant-session'
import { clearBConsoleSession, setBConsoleSession } from '@/views/merchant/registry/session'
import { STORE_BASE, storePath } from '@/views/merchant/registry/paths'

/**
 * 登录后进入商家管理中心（方案 H：一壳多模块）
 */
export async function afterMerchantLogin(
  router: Router,
  accountId?: string | number,
  redirect?: string
) {
  clearBConsoleSession()
  clearMerchantSession()
  try {
    const ctx = await getWorkbenchContext()
    setMerchantSession({
      merchantId: String(ctx.merchantId),
      merchantName: ctx.shopName || ctx.merchantName,
      shopName: ctx.shopName,
      modules: ctx.modules || [],
      permissions: ctx.permissions || [],
      mallBizMode: ctx.mallBizMode || undefined
    })
    localStorage.setItem('merchantId', String(ctx.accountId))
    // 兼容旧页：写入各模块 subjectId
    for (const m of ctx.modules || []) {
      if (m.moduleId === 'CAPACITY') {
        localStorage.setItem('capacityOrgId', m.subjectId)
      }
      if (m.moduleId === 'O2O') {
        localStorage.setItem('merchantInstitutionId', m.subjectId)
      }
      if (m.moduleId === 'MALL') {
        localStorage.setItem('merchantShopId', m.subjectId)
      }
      if (m.moduleId === 'HOTEL') {
        localStorage.setItem('hotelShopId', m.subjectId)
      }
      if (m.moduleId === 'SCENIC') {
        // 景区复用商城店铺主体；无独立 shop 时回落 subjectId
        if (m.subjectId) {
          localStorage.setItem('scenicShopId', m.subjectId)
          if (!localStorage.getItem('merchantShopId')) {
            localStorage.setItem('merchantShopId', m.subjectId)
          }
        }
      }
    }
    const home =
      redirect &&
      isRedirectAllowedH(
        redirect,
        (ctx.modules || []).map((m) => m.moduleId)
      )
        ? redirect
        : (ctx.modules || []).length > 0
          ? storePath('/workbench')
          : storePath('/workbench')
    await router.replace(home)
  } catch {
    ElMessage.error('加载商家工作台失败，请重试')
    await router.replace(storePath('/login'))
  }
}

function isRedirectAllowedH(path: string, moduleIds: string[]): boolean {
  if (path.includes('/apply')) return false
  if (path === storePath('/workbench')) return true
  for (const mid of moduleIds) {
    if (
      mid === 'MALL' &&
      path.startsWith(STORE_BASE) &&
      !path.startsWith(storePath('/o2o')) &&
      !path.startsWith(storePath('/capacity')) &&
      !path.startsWith(storePath('/hotel')) &&
      !path.startsWith(storePath('/scenic')) &&
      !path.startsWith(storePath('/rental'))
    ) {
      return true
    }
    if (mid === 'O2O' && path.startsWith(storePath('/o2o'))) return true
    if (mid === 'CAPACITY' && path.startsWith(storePath('/capacity'))) return true
    if (mid === 'HOTEL' && path.startsWith(storePath('/hotel'))) return true
    if (mid === 'SCENIC' && path.startsWith(storePath('/scenic'))) return true
    if (mid === 'RENTAL' && path.startsWith(storePath('/rental'))) return true
    if (mid === 'XUANXUE' && path.startsWith(storePath('/xuanxue'))) return true
  }
  return false
}

/** @deprecated 选台已非主路径，保留兼容 */
export async function enterAndGo(
  router: Router,
  binding: BConsoleBindingVO,
  accountId?: string | number,
  redirect?: string
) {
  const entered = await enterBConsole({
    consoleId: binding.consoleId,
    subjectId: binding.subjectId
  })
  setBConsoleSession({
    consoleId: entered.consoleId,
    subjectId: entered.subjectId,
    accountId: accountId != null ? String(accountId) : undefined
  })
  if (entered.consoleId === 'CAPACITY') {
    localStorage.setItem('capacityOrgId', entered.subjectId)
  }
  if (entered.consoleId === 'O2O_ORG') {
    localStorage.setItem('merchantInstitutionId', entered.subjectId)
  }
  if (entered.consoleId === 'MALL_SHOP') {
    localStorage.setItem('merchantShopId', entered.subjectId)
  }
  if (entered.consoleId === 'HOTEL_SHOP') {
    localStorage.setItem('hotelShopId', entered.subjectId)
  }
  if (entered.consoleId === 'RENTAL_SHOP') {
    localStorage.setItem('rentalShopId', entered.subjectId)
  }
  if (entered.consoleId === 'XUANXUE_SHOP') {
    localStorage.setItem('xuanxueShopId', entered.subjectId)
  }
  const target =
    redirect && isRedirectAllowedForConsole(redirect, entered.consoleId)
      ? redirect
      : entered.homePath
  await router.replace(target)
}

function isRedirectAllowedForConsole(path: string, consoleId: string): boolean {
  if (path.includes('/apply')) return false
  if (consoleId === 'MALL_SHOP') {
    return (
      path.startsWith('/merchant') &&
      !path.startsWith('/merchant/o2o') &&
      !path.startsWith('/merchant/capacity') &&
      !path.startsWith('/store/rental')
    )
  }
  if (consoleId === 'O2O_ORG') return path.startsWith('/merchant/o2o')
  if (consoleId === 'CAPACITY') return path.startsWith('/merchant/capacity')
  if (consoleId === 'HOTEL_SHOP') return path.startsWith('/store/hotel')
  if (consoleId === 'RENTAL_SHOP') return path.startsWith('/store/rental')
  if (consoleId === 'XUANXUE_SHOP') return path.startsWith('/store/xuanxue')
  return false
}

export async function switchConsole(router: Router) {
  clearBConsoleSession()
  clearMerchantSession()
  await router.replace(storePath('/login'))
}

export { moduleIdFromConsole }
